import { useEffect, useMemo, useState } from "react";
import { Printer, QrCode } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { renderLabels, renderLabelsWithOffset, LABEL_LAYOUT } from "../../lib/qrLabels";

/**
 * QrPrintDialog -- PDF-Druck für Artikel- und Asset-QR-Codes.
 *
 * Props:
 *   open, onClose
 *   title -- Dialog-Titel
 *   kind -- 'article' | 'asset'
 *   items -- Array mit den Pflichtfeldern id, title und subtitle
 *   defaultSelection -- optional Array von ids die vorausgewählt sind
 */
export function QrPrintDialog({
    open,
    onClose,
    title = "QR-Sticker drucken",
    kind,
    items = [],
    defaultSelection = null,
    testId = "qr-print-dialog",
}) {
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(new Set());
    const [startSlot, setStartSlot] = useState(0);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (defaultSelection && defaultSelection.length) {
            setSelected(new Set(defaultSelection));
        } else {
            setSelected(new Set());
        }
        setSearch("");
        setStartSlot(0);
    }, [open, defaultSelection]);

    const filtered = useMemo(() => {
        if (!search) return items;
        const s = search.toLowerCase();
        return items.filter(
            (it) =>
                (it.title || "").toLowerCase().includes(s) ||
                (it.subtitle || "").toLowerCase().includes(s) ||
                (it.hint || "").toLowerCase().includes(s) ||
                (it.id || "").toLowerCase().includes(s)
        );
    }, [items, search]);

    const toggle = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelected(next);
    };
    const allOnPage = filtered.every((it) => selected.has(it.id));
    const toggleAll = () => {
        if (allOnPage) {
            const next = new Set(selected);
            for (const it of filtered) next.delete(it.id);
            setSelected(next);
        } else {
            const next = new Set(selected);
            for (const it of filtered) next.add(it.id);
            setSelected(next);
        }
    };

    const chosen = items.filter((it) => selected.has(it.id));
    const pages = Math.ceil((chosen.length + Number(startSlot || 0)) / LABEL_LAYOUT.perPage);

    const print = async () => {
        if (chosen.length === 0) return toast.error("Mind. 1 Etikett auswählen.");
        setBusy(true);
        try {
            const mapped = chosen.map((it) => ({ ...it, kind }));
            const fname = `platzwart-qr-${kind}-${new Date()
                .toISOString()
                .slice(0, 10)}.pdf`;
            if (Number(startSlot) > 0) {
                await renderLabelsWithOffset(mapped, Number(startSlot), fname);
            } else {
                await renderLabels(mapped, fname);
            }
            toast.success(`${chosen.length} Sticker auf ${pages} Seite(n) erstellt.`);
            onClose?.();
        } catch (e) {
            toast.error(`PDF-Fehler: ${e.message}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-2xl" data-testid={testId}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <QrCode className="h-5 w-5" /> {title}
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        {LABEL_LAYOUT.format} · {LABEL_LAYOUT.label} · {LABEL_LAYOUT.perPage}{" "}
                        Etiketten pro A4-Bogen
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-wrap items-center gap-2">
                    <Input
                        placeholder="Filter..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="h-9 w-64"
                        data-testid={`${testId}-search`}
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={toggleAll}
                        data-testid={`${testId}-toggle-all`}
                    >
                        {allOnPage ? "Alle abwählen" : "Alle wählen"}
                    </Button>
                    <span className="ml-auto text-xs text-muted-foreground">
                        {selected.size} / {items.length} gewählt · {pages} Seite(n)
                    </span>
                </div>

                <div className="max-h-80 overflow-auto rounded-md border border-border/60 bg-card/30">
                    {filtered.length === 0 ? (
                        <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                            Keine Einträge.
                        </div>
                    ) : (
                        <ul className="divide-y divide-border/40">
                            {filtered.map((it) => (
                                <li
                                    key={it.id}
                                    className="flex items-center gap-3 px-3 py-2 text-sm"
                                >
                                    <Checkbox
                                        checked={selected.has(it.id)}
                                        onCheckedChange={() => toggle(it.id)}
                                        data-testid={`${testId}-row-${it.id}`}
                                    />
                                    <div className="flex-1">
                                        <div className="font-medium text-foreground">
                                            {it.title}
                                        </div>
                                        {it.subtitle || it.hint ? (
                                            <div className="text-xs text-muted-foreground">
                                                {it.subtitle || it.hint}
                                            </div>
                                        ) : null}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="flex items-center gap-3">
                    <Label className="whitespace-nowrap text-xs text-muted-foreground">
                        Start-Position auf Bogen
                    </Label>
                    <Input
                        type="number"
                        min={0}
                        max={LABEL_LAYOUT.perPage - 1}
                        value={startSlot}
                        onChange={(e) => setStartSlot(e.target.value)}
                        className="h-9 w-20"
                        data-testid={`${testId}-start-slot`}
                    />
                    <span className="text-xs text-muted-foreground">
                        0 = oben links. Nützlich wenn der Bogen teilweise benutzt ist.
                    </span>
                </div>

                <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => onClose?.()}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={print}
                        disabled={busy || chosen.length === 0}
                        data-testid={`${testId}-print`}
                    >
                        <Printer className="mr-1 h-4 w-4" />
                        {busy ? "erstelle..." : `PDF erstellen (${chosen.length})`}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
